import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';

function WhatsAppBrandIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm0 18.15c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.32a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c.02 4.54-3.68 8.24-8.22 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.22.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.73 2.64 4.18 3.7.58.25 1.04.4 1.39.51.59.19 1.12.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.22-.16-.47-.28z" />
    </svg>
  );
}

export default function BotSetupProgress({ bot, onCompleteSetup }) {
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);

  const isWhatsApp = bot.id === 'whatsapp';

  const handleSimulateCompletion = () => {
    setIsSimulating(true);
    setTimeout(() => {
      setIsSimulating(false);
      toast.success(`${bot.name} is now live!`);
      if (onCompleteSetup) {
        onCompleteSetup(bot.id);
      }
    }, 600);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {/* 1. Brand Squircle Icon matching unactivated state */}
      <Box
        sx={{
          width: { xs: 72, sm: 80 },
          height: { xs: 72, sm: 80 },
          borderRadius: '22px',
          bgcolor: isWhatsApp ? '#25D366' : bot.brandBg,
          color: isWhatsApp ? '#FFFFFF' : bot.brandColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isWhatsApp
            ? '0 10px 24px rgba(37, 211, 102, 0.28)'
            : `0 10px 24px ${bot.brandColor}25`,
        }}
      >
        {isWhatsApp ? <WhatsAppBrandIcon /> : bot.icon}
      </Box>

      {/* 2. Title & Subtitle */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          fontSize: { xs: '2rem', sm: '2.4rem' },
          color: '#111827',
          letterSpacing: '-0.03em',
          mt: 3,
          lineHeight: 1.15,
        }}
      >
        {bot.name}
      </Typography>

      <Typography
        sx={{
          fontSize: { xs: '1.05rem', sm: '1.15rem' },
          color: '#6B7280',
          fontWeight: 400,
          mt: 1,
          lineHeight: 1.4,
        }}
      >
        Your bot is being set up.
      </Typography>

      {/* 3. Focal Status: Big "Setting Up" with soft pulsing amber indicator */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1.5}
        sx={{ mt: { xs: 4, sm: 5 } }}
      >
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: '#F59E0B',
            boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.22)',
            animation: 'pulse 1.8s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { transform: 'scale(0.9)', opacity: 0.7 },
              '50%': { transform: 'scale(1.2)', opacity: 1 },
              '100%': { transform: 'scale(0.9)', opacity: 0.7 },
            },
          }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2.8rem', sm: '3.4rem' },
            color: '#111827',
            letterSpacing: '-0.04em',
            lineHeight: 1.05,
          }}
        >
          Setting Up
        </Typography>
      </Stack>

      {/* 4. Subtitle / Estimated Time Note */}
      <Typography
        sx={{
          fontSize: { xs: '0.88rem', sm: '0.95rem' },
          color: '#6B7280',
          fontWeight: 400,
          mt: 1.5,
        }}
      >
        Estimated turnaround: 2 to 4 hours.
      </Typography>

      {/* 5. Apple Pill Button */}
      <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 } }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => setSupportModalOpen(true)}
          sx={{
            py: 1.6,
            borderRadius: '999px',
            fontSize: '1.05rem',
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: '#111827',
            color: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#1F2937',
              boxShadow: 'none',
            },
          }}
        >
          Contact Support
        </Button>
      </Box>

      {/* 6. Footer Reassurance */}
      <Typography
        sx={{
          fontSize: { xs: '0.82rem', sm: '0.88rem' },
          color: '#86868B',
          mt: 1.75,
        }}
      >
        We&apos;ll send an email once your bot is live. No action needed.
      </Typography>

      {/* 7. Subtle Demo Simulation Action */}
      {onCompleteSetup && (
        <Button
          size="small"
          onClick={handleSimulateCompletion}
          disabled={isSimulating}
          sx={{
            mt: 3,
            color: '#9CA3AF',
            fontSize: '0.78rem',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': { color: '#111827', bgcolor: 'transparent' },
          }}
        >
          {isSimulating ? 'Activating...' : 'Simulate Go Live (Demo)'}
        </Button>
      )}

      {/* Support Dialog */}
      <Dialog
        open={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '24px', p: 1 },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Super Admin Support
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          <Stack spacing={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Our engineering team is actively working on your bot setup:
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '16px',
                bgcolor: '#F9FAFB',
                textAlign: 'left',
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Order Ref:
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#111827">
                    #ALIS-BOT-8820
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Bot:
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#111827">
                    {bot.name}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    WhatsApp Desk:
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#25D366">
                    +234 814 000 0000
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Email:
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="#0071E3">
                    setup@alisbuild.com
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Typography variant="caption" color="text.secondary">
              Typical response time is under 15 minutes.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setSupportModalOpen(false)}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#111827',
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1F2937' },
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

BotSetupProgress.propTypes = {
  bot: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    brandColor: PropTypes.string.isRequired,
    brandBg: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
  }).isRequired,
  onCompleteSetup: PropTypes.func,
};
